'use client';

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetClubResourcesQuery,
  useGetPortWatchShiftsByClubQuery,
} from '../../services/ClubResourceService';
import { useGetAllClubBookingsQuery } from '../../services/ClubBookingService';
import {
  CalendarEvent,
  AssetType,
  ViewType,
} from '../habor-management/components/types';
import {
  PriceUnit,
  ResourceType as ClubResourceType,
  IClubResourceData,
} from '../../types/club-resource.types';
import {
  ClubBookingStatus,
  ResourceType as BookingResourceType,
} from '../../types/club-booking-management.types';
import StatusCards from '../habor-management/components/StatusCards';
import FilterSection from '../habor-management/components/FilterSection';
import CalendarView from '../habor-management/components/CalendarView';
import EventModal from '../habor-management/components/EventModal';
import DayEventsModal from '../habor-management/components/DayEventsModal';
import { PortWatchShiftStatus } from '@/constant';

const formatDateDay = (d: Date) => d.toISOString().substring(0, 10);

const QUERY_LIMIT = 1000;
const POLLING_INTERVAL_MS = 30000;

// Extended CalendarEvent interface for club bookings
interface ExtendedCalendarEvent extends CalendarEvent {
  bookingId?: string;
  itemId?: string;
  specialRequests?: string | null;
  cancellationReason?: string | null;
}

interface ClubResourceSlot {
  id?: string;
  date: string;
  start_time?: string;
  end_time?: string;
}

type ClubResourceWithSlots = IClubResourceData & {
  allow_bookings?: boolean;
  slots?: ClubResourceSlot[];
};

export default function ClubAvailabilityCalendar() {
  const { id: clubId } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('week');
  const [statusFilters, setStatusFilters] = useState<Set<string>>(
    new Set([
      'confirmed',
      'payment-pending',
      'pending-confirmation',
      'completed',
      'cancelled',
      'available',
      'blocked',
      'filled',
    ])
  );
  const [assetTypeFilters, setAssetTypeFilters] = useState<Set<AssetType>>(
    new Set(['resource']) // Use resource type for club resources
  );
  const [selectedEvent, setSelectedEvent] =
    useState<ExtendedCalendarEvent | null>(null);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState<{
    open: boolean;
    date?: Date;
    events?: CalendarEvent[];
  }>({ open: false });
  const [userFilter, setUserFilter] = useState<string>('');
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [selectedResource, setSelectedResource] = useState<string | null>(null);

  // Fetch club resources
  const { data: clubResourcesResponse, isLoading: isResourcesLoading } =
    useGetClubResourcesQuery(
      {
        page: 1,
        limit: QUERY_LIMIT,
        club_id: clubId,
      },
      { skip: !clubId, pollingInterval: POLLING_INTERVAL_MS }
    );

  // Fetch club bookings
  const { data: clubBookingsResponse, isLoading: isBookingsLoading } =
    useGetAllClubBookingsQuery(
      {
        page: 1,
        limit: QUERY_LIMIT,
        club_id: clubId,
      },
      { skip: !clubId, pollingInterval: POLLING_INTERVAL_MS }
    );

  // Fetch Port Watch shifts (filled and completed) for the club
  const { data: portWatchShiftsResponse, isLoading: isShiftsLoading } =
    useGetPortWatchShiftsByClubQuery(clubId || '', {
      skip: !clubId,
      pollingInterval: POLLING_INTERVAL_MS,
    });

  // Transform club bookings into calendar events
  const bookingEvents: ExtendedCalendarEvent[] = useMemo(() => {
    if (
      !clubBookingsResponse?.data ||
      !Array.isArray(clubBookingsResponse.data)
    )
      return [];

    interface BookingItem {
      id: string;
      resource: {
        id: string;
        name: string;
        resource_type: BookingResourceType;
      };
      start_time: string;
      end_time: string;
      status: ClubBookingStatus;
      total_price: number;
      quantity: number | null;
      special_requests: string | null;
      cancellation_reason: string | null;
    }

    interface BookingData {
      id: string;
      user: {
        name: string | null;
        email: string;
      };
      items: BookingItem[];
    }

    return (clubBookingsResponse.data as BookingData[]).flatMap((booking) =>
      booking.items.map((item) => {
        // Map club booking status to calendar event status
        const getCalendarStatus = (status: ClubBookingStatus): string => {
          switch (status) {
            case ClubBookingStatus.CONFIRMED:
              return 'confirmed';
            case ClubBookingStatus.PENDING_CONFIRMATION:
              return 'pending-confirmation';
            case ClubBookingStatus.PENDING:
              return 'payment-pending';
            case ClubBookingStatus.COMPLETED:
              return 'completed';
            case ClubBookingStatus.CANCELLED:
              return 'cancelled';
            default:
              return 'pending-confirmation';
          }
        };

        return {
          id: `booking-${booking.id}-${item.id}`,
          title: `${item.resource.name} - ${booking.user.name || booking.user.email}`,
          start: item.start_time.split('T')[0], // Extract date part
          end: item.end_time.split('T')[0],
          status: getCalendarStatus(item.status),
          berthNumber: '-',
          user: {
            name: booking.user.name || booking.user.email,
            email: booking.user.email,
          },
          assetType: 'resource' as AssetType,
          resource: {
            id: item.resource.id || '',
            name: item.resource.name,
            description: item.special_requests || '',
            price: item.total_price,
            price_unit: PriceUnit.PER_DAY,
            is_available: item.status === ClubBookingStatus.CONFIRMED,
            harbor_id: '',
            quantity: item.quantity || 1,
            is_standalone: false,
            start_time: item.start_time.split('T')[0],
            end_time: item.end_time.split('T')[0],
            status: 1,
          },
          price: item.total_price,
          priceUnit: PriceUnit.PER_DAY,
          bookingId: booking.id,
          itemId: item.id,
          specialRequests: item.special_requests,
          cancellationReason: item.cancellation_reason,
        } as ExtendedCalendarEvent;
      })
    );
  }, [clubBookingsResponse]);

  // Transform Port Watch shifts into calendar events
  const portWatchEvents: ExtendedCalendarEvent[] = useMemo(() => {
    const shifts = portWatchShiftsResponse?.data || [];
    if (!Array.isArray(shifts) || shifts.length === 0) return [];

    return shifts.map((shift) => {
      const assignedName =
        shift.assigned_member?.name ||
        shift.assigned_member?.email ||
        'Unassigned';
      const assignedEmail = shift.assigned_member?.email || '';
      const startDate = shift.shift_start.split('T')[0];
      const endDate = shift.shift_end.split('T')[0];
      let statusStr: CalendarEvent['status'] = 'confirmed';
      if (shift.status === PortWatchShiftStatus.COMPLETED)
        statusStr = 'completed';
      else if (shift.status === PortWatchShiftStatus.FILLED)
        statusStr = 'filled';

      return {
        id: `portwatch-${shift.id}`,
        title: `${shift.resource?.name || 'Port Watch'} - ${assignedName}`,
        start: startDate,
        end: endDate,
        status: statusStr,
        berthNumber: '-',
        user: {
          name: assignedName,
          email: assignedEmail,
        },
        assetType: 'resource' as AssetType,
        resource: {
          id: shift.resource?.id || '',
          name: shift.resource?.name || 'Port Watch',
          description: '',
          price: shift.resource?.price || 0,
          price_unit: shift.resource?.price_unit || PriceUnit.PER_DAY,
          is_available: false,
          harbor_id: '',
          quantity: shift.resource?.quantity || 1,
          is_standalone: false,
          start_time: startDate,
          end_time: endDate,
          status: 1,
          resource_type: ClubResourceType.PORT_WATCH_SHIFTS,
        },
        price: 0,
        priceUnit: PriceUnit.PER_DAY,
      } as ExtendedCalendarEvent;
    });
  }, [portWatchShiftsResponse]);

  // Combine backend-driven events (bookings + port watch shifts)
  const backendEvents: ExtendedCalendarEvent[] = useMemo(
    () => [...bookingEvents, ...portWatchEvents],
    [bookingEvents, portWatchEvents]
  );

  // Generate available club resource events
  const availableResourceEvents: CalendarEvent[] = useMemo(() => {
    if (!clubResourcesResponse?.data) return [];

    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const resources = clubResourcesResponse.data as ClubResourceWithSlots[];

    return resources
      .filter((resource) => {
        const allow = resource.allow_booking ?? resource.allow_bookings;
        return !!allow && resource.status === 1;
      })
      .flatMap((resource) => {
        const slots = Array.isArray(resource.slots) ? resource.slots : [];

        if (slots.length > 0) {
          return slots.map((slot, idx) => ({
            id: `available-resource-${resource.id}-${slot.id || idx}`,
            title: `${resource.name} (Available)`,
            start: slot.date,
            end: slot.date,
            status: 'available',
            berthNumber: '-',
            user: { name: '', email: '' },
            isAvailable: true,
            assetType: 'resource' as AssetType,
            resource: {
              ...resource,
              id: resource.id || '',
              name: resource.name,
              description: resource.description || '',
              price: resource.price || 0,
              price_unit: resource.price_unit || PriceUnit.PER_DAY,
              is_available: true,
              harbor_id: '',
              quantity: resource.quantity || 1,
              is_standalone: false,
              start_time: slot.date,
              end_time: slot.date,
              status: resource.status,
            },
            price: resource.price || 0,
            priceUnit: resource.price_unit || PriceUnit.PER_DAY,
            startTime: slot.start_time,
            endTime: slot.end_time,
          }));
        }

        // Fallback: if backend doesn't provide slots, show a wide availability range
        return [
          {
            id: `available-resource-${resource.id}`,
            title: `${resource.name} (Available)`,
            start: formatDateDay(today),
            end: formatDateDay(endOfMonth),
            status: 'available',
            berthNumber: '-',
            user: { name: '', email: '' },
            isAvailable: true,
            assetType: 'resource' as AssetType,
            resource: {
              ...resource,
              id: resource.id || '',
              name: resource.name,
              description: resource.description || '',
              price: resource.price || 0,
              price_unit: resource.price_unit || PriceUnit.PER_DAY,
              is_available: true,
              harbor_id: '',
              quantity: resource.quantity || 1,
              is_standalone: false,
              start_time: formatDateDay(today),
              end_time: formatDateDay(endOfMonth),
              status: resource.status,
            },
            price: resource.price || 0,
            priceUnit: resource.price_unit || PriceUnit.PER_DAY,
          },
        ];
      });
  }, [clubResourcesResponse]);

  // Generate blocked club resource events
  const blockedResourceEvents: CalendarEvent[] = useMemo(() => {
    if (!clubResourcesResponse?.data) return [];

    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const resources = clubResourcesResponse.data as ClubResourceWithSlots[];

    return resources
      .filter((resource) => {
        const allow = resource.allow_booking ?? resource.allow_bookings;
        return !allow || resource.status !== 1;
      })
      .map((resource) => ({
        id: `blocked-resource-${resource.id}`,
        title: `${resource.name} (Unavailable)`,
        start: formatDateDay(today),
        end: formatDateDay(endOfMonth),
        status: 'blocked',
        berthNumber: '-',
        user: { name: 'Unavailable', email: '' },
        isBlocked: true,
        assetType: 'resource' as AssetType,
        resource: {
          ...resource,
          id: resource.id || '',
          name: resource.name,
          description: resource.description || '',
          price: resource.price || 0,
          price_unit: resource.price_unit || PriceUnit.PER_DAY,
          is_available: false,
          harbor_id: '',
          quantity: resource.quantity || 1,
          is_standalone: false,
          start_time: formatDateDay(today),
          end_time: formatDateDay(endOfMonth),
          status: resource.status,
        },
        price: resource.price || 0,
        priceUnit: resource.price_unit || PriceUnit.PER_DAY,
      }));
  }, [clubResourcesResponse]);

  const allEvents = useMemo(() => {
    return [
      ...backendEvents,
      ...availableResourceEvents,
      ...blockedResourceEvents,
    ];
  }, [backendEvents, availableResourceEvents, blockedResourceEvents]);

  const filteredEvents = useMemo(
    () =>
      allEvents.filter((ev) => {
        if (!statusFilters.has(ev.status)) return false;

        if (selectedAssetType) {
          if (ev.assetType !== selectedAssetType) return false;
        } else {
          if (ev.assetType && !assetTypeFilters.has(ev.assetType)) return false;
        }

        if (
          userFilter &&
          !(
            ev.user?.name?.toLowerCase().includes(userFilter.toLowerCase()) ||
            ev.user?.email?.toLowerCase().includes(userFilter.toLowerCase())
          )
        )
          return false;

        // Specific resource selection
        if (selectedResource) {
          if (ev.resource?.id !== selectedResource) return false;
        }

        return true;
      }),
    [
      allEvents,
      statusFilters,
      assetTypeFilters,
      userFilter,
      selectedAssetType,
      selectedResource,
    ]
  );

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const toggleAssetTypeFilter = (assetType: AssetType) => {
    setAssetTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(assetType)) next.delete(assetType);
      else next.add(assetType);
      return next;
    });
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const getStatusCount = (status: string, useFiltered = false) =>
    (useFiltered ? filteredEvents : allEvents).filter(
      (e) => e.status === status
    ).length;

  const getResourceOptions = () => {
    if (!clubResourcesResponse?.data) return [];

    return clubResourcesResponse.data.map((resource) => ({
      value: resource.id || '',
      label: resource.name,
    }));
  };

  const getResourceName = (resourceId: string | null): string => {
    if (!resourceId || !clubResourcesResponse?.data) return 'Resource';

    const resource = clubResourcesResponse.data.find(
      (r) => r.id === resourceId
    );
    return resource?.name || 'Resource';
  };

  if (!clubId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Club Selected
          </h3>
          <p className="text-gray-600">
            Please select a club to view its availability calendar.
          </p>
        </div>
      </div>
    );
  }

  const handleEditBooking = () => {
    if (selectedEvent?.bookingId) {
      navigate(`/club-booking-management/${selectedEvent.bookingId}/edit`);
    }
  };

  const handleViewBooking = () => {
    if (selectedEvent?.bookingId) {
      navigate(`/club-booking-management/${selectedEvent.bookingId}`);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-4">
        {/* Status Cards */}
        <StatusCards
          filteredEvents={filteredEvents}
          backendEvents={backendEvents}
          availableBerthCount={0}
          availableAddonCount={0}
        />

        {/* Filters Section */}
        <FilterSection
          statusFilters={statusFilters}
          toggleStatusFilter={toggleStatusFilter}
          assetTypeFilters={assetTypeFilters}
          toggleAssetTypeFilter={toggleAssetTypeFilter}
          userFilter={userFilter}
          setUserFilter={setUserFilter}
          selectedAssetType={selectedAssetType}
          setSelectedAssetType={setSelectedAssetType}
          selectedBerth={selectedResource}
          setSelectedBerth={setSelectedResource}
          resetFilters={() => {
            setSelectedAssetType(null);
            setSelectedResource(null);
            setAssetTypeFilters(new Set(['resource']));
            setStatusFilters(
              new Set([
                'confirmed',
                'payment-pending',
                'pending-confirmation',
                'completed',
                'cancelled',
                'available',
                'blocked',
                'filled',
              ])
            );
            setUserFilter('');
          }}
          getStatusCount={getStatusCount}
          berthOptions={[]} // No berths for club resources
          addonOptions={getResourceOptions()}
          getAddonName={getResourceName}
          resourceOptions={getResourceOptions()}
          getResourceName={getResourceName}
        />

        {/* Loading State */}
        {(isResourcesLoading || isBookingsLoading || isShiftsLoading) && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">
              Loading{' '}
              {isResourcesLoading && (isBookingsLoading || isShiftsLoading)
                ? 'resources and bookings'
                : isBookingsLoading || isShiftsLoading
                  ? 'bookings'
                  : 'resources'}
              ...
            </span>
          </div>
        )}

        {/* Empty State */}
        {filteredEvents.length === 0 &&
          !isResourcesLoading &&
          !isBookingsLoading &&
          !isShiftsLoading && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No club resources found
              </h3>
              <p className="text-gray-600 mb-4">
                No club resources match your current filters.
              </p>
              <button
                onClick={() => {
                  setSelectedAssetType(null);
                  setSelectedResource(null);
                  setAssetTypeFilters(new Set(['resource']));
                  setStatusFilters(
                    new Set([
                      'confirmed',
                      'payment-pending',
                      'pending-confirmation',
                      'completed',
                      'cancelled',
                      'available',
                      'blocked',
                      'filled',
                    ])
                  );
                  setUserFilter('');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Reset Filters
              </button>
            </div>
          )}

        {/* Calendar View */}
        {filteredEvents.length > 0 && (
          <CalendarView
            currentDate={currentDate}
            view={view}
            setView={setView}
            filteredEvents={filteredEvents}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            handleToday={handleToday}
            handleEventClick={handleEventClick}
            onMoreClick={(date, events) =>
              setDayEventsModal({ open: true, date, events })
            }
          />
        )}

        {/* Modals */}
        <EventModal
          event={selectedEvent}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCancel={() => {
            enqueueSnackbar(
              'Club resource booking cancellation not implemented',
              {
                variant: 'info',
              }
            );
          }}
          cancelReason=""
          setCancelReason={() => {}}
          isSubmitting={false}
          onEdit={handleEditBooking}
          onView={handleViewBooking}
        />

        <DayEventsModal
          isOpen={dayEventsModal.open}
          date={dayEventsModal.date}
          events={dayEventsModal.events}
          onClose={() => setDayEventsModal({ open: false })}
          onEventClick={(ev) => {
            setSelectedEvent(ev);
            setIsModalOpen(true);
            setDayEventsModal({ open: false });
          }}
        />
      </div>
    </div>
  );
}
